import { AdminAnalyticsActivityType } from '~/api/graphql';
import type { MetricProgressVariant } from '~/components/settings/analytics/metric-progress-bar';

/**
 * Design tokens for the admin analytics dashboard.
 *
 * Tailwind theme: primary = sky, secondary = emerald, accent = amber.
 * SVG charts use hex values that must match the corresponding Tailwind shade.
 *
 * Dark mode: analytics.tsx intentionally omits dark: variants; settings chrome
 * renders this page in light mode only.
 */

export type ActivityPaletteEntry = {
  text: string;
  hex: string;
  soft: string;
};

/** Hex + Tailwind utilities shared by SVG charts and HTML legend pills. */
export const ACTIVITY_PALETTE: Record<AdminAnalyticsActivityType, ActivityPaletteEntry> = {
  [AdminAnalyticsActivityType.Workspace]: {
    text: 'text-violet-700 dark:text-violet-300',
    hex: '#8B5CF6',
    soft: '#EDE9FE'
  },
  [AdminAnalyticsActivityType.Call]: {
    text: 'text-primary-700 dark:text-primary-300',
    hex: '#0EA5E9',
    soft: '#E0F2FE'
  },
  [AdminAnalyticsActivityType.Email]: {
    text: 'text-secondary-700 dark:text-secondary-300',
    hex: '#10B981',
    soft: '#D1FAE5'
  },
  [AdminAnalyticsActivityType.KnowledgeBaseUpload]: {
    text: 'text-accent-700 dark:text-accent-300',
    hex: '#F59E0B',
    soft: '#FEF3C7'
  }
};

export type StatCardAccent = 'violet' | 'primary' | 'secondary' | 'accent';

/** Icon background tints for stat cards (mirrors Badge opacity pattern). */
export const STAT_CARD_ACCENT_CLASSES: Record<StatCardAccent, string> = {
  violet: 'bg-violet-500/15 text-violet-700 dark:text-violet-300',
  primary: 'bg-primary-500/15 text-primary-700 dark:text-primary-300',
  secondary: 'bg-secondary-500/15 text-secondary-700 dark:text-secondary-300',
  accent: 'bg-accent-500/15 text-accent-700 dark:text-accent-300'
};

/** Donut chart slice colors aligned with activity / semantic tokens. */
export const WORKSPACE_PALETTE = [
  ACTIVITY_PALETTE[AdminAnalyticsActivityType.Workspace].hex,
  ACTIVITY_PALETTE[AdminAnalyticsActivityType.Call].hex,
  ACTIVITY_PALETTE[AdminAnalyticsActivityType.Email].hex,
  ACTIVITY_PALETTE[AdminAnalyticsActivityType.KnowledgeBaseUpload].hex,
  '#EC4899', // pink — distinct fifth slice
  '#64748B' // zinc — the "Other" bucket
] as const;

export type TemplateBadgeColor = 'blue' | 'sky' | 'emerald' | 'amber' | 'pink' | 'zinc';

/** Maps Badge color prop values to progress bar gradient variants. */
export const TEMPLATE_BADGE_TO_PROGRESS: Record<TemplateBadgeColor, MetricProgressVariant> = {
  sky: 'primary',
  emerald: 'secondary',
  amber: 'accent',
  blue: 'blue',
  pink: 'pink',
  zinc: 'zinc'
};
